import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui-kit/sheet';
import { Badge } from '@/components/ui-kit/badge';
import { Separator } from '@/components/ui-kit/separator';
import { formatDate } from '@/lib/utils/custom-date/custom-date';
import { ActivityLog } from '../../types/activity-log.types';

interface ActivityLogDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  log: ActivityLog | null;
}

/** Pretty-prints ChangesJson, falling back to the raw string if it isn't valid JSON. */
const formatChanges = (t: (key: string) => string, changesJson?: string): string => {
  if (!changesJson) return t('LOGS_DETAIL_NO_CHANGES');
  try {
    return JSON.stringify(JSON.parse(changesJson), null, 2);
  } catch {
    return changesJson;
  }
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
    <span className="break-words text-sm text-foreground">{value || '-'}</span>
  </div>
);

export const ActivityLogDetailDrawer = ({
  open,
  onOpenChange,
  log,
}: Readonly<ActivityLogDetailDrawerProps>) => {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('LOGS_DETAIL_TITLE')}</SheetTitle>
          <SheetDescription>{t('LOGS_DETAIL_DESCRIPTION')}</SheetDescription>
        </SheetHeader>

        {log && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
              <DetailRow
                label={t('LOGS_COLUMN_TIMESTAMP')}
                value={log.CreatedDate ? formatDate(new Date(log.CreatedDate)) : ''}
              />
              <DetailRow
                label={t('LOGS_COLUMN_ACTION')}
                value={log.Action ? t(String(log.Action).toUpperCase()) : ''}
              />
              <DetailRow label={t('LOGS_COLUMN_MODULE')} value={log.ModuleName} />
              <DetailRow label={t('LOGS_COLUMN_ENTITY')} value={log.EntityType} />
              <DetailRow label={t('LOGS_DETAIL_ENTITY_ID')} value={log.EntityId} />
              <DetailRow label={t('LOGS_COLUMN_PERFORMED_BY')} value={log.PerformedByName || log.PerformedBy} />
            </div>

            <Separator />

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">
                {t('LOGS_COLUMN_SUMMARY')}
              </span>
              <p className="break-words text-sm text-foreground">{log.Summary || '-'}</p>
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  {t('LOGS_DETAIL_CHANGES_LABEL')}
                </span>
                <Badge variant="outline">{t('LOGS_DETAIL_FORMAT_JSON')}</Badge>
              </div>
              <pre className="max-h-[50vh] overflow-auto rounded-md bg-muted p-3 text-xs">
                {formatChanges(t, log.ChangesJson)}
              </pre>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
